import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import type { ActorRefFrom } from 'xstate';
import { Container, Footer, FormContent, Header } from '../components/Layout';
import { PAGES } from '../constants';
import { CVUploadForm } from '../forms/UploadForm';
import { providerRegistrationView } from '../registration-view.machine';
import type { RegistrationViewContext } from '../types';

interface PageProps {
  viewContext: RegistrationViewContext;
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
}

const cvUploadTags = CT.registration.cvUpload;
const footerTags = {
  back: cvUploadTags.backButton,
  loadingBar: cvUploadTags.loadingBar,
};

export function CVUploadPage({ actorRef }: PageProps) {
  const {
    uploaded,
    documentReference,
    docType: machineDocType,
  } = useSelector(actorRef, (state) => state.context.cvUpload);

  // Still load-bearing after MCV-1593 removed the pre-upload pill toggle: the
  // type is chosen *after* the file is selected, but the success card and the
  // "Reading your credentialing packet..." copy still read it back out.
  const docType = machineDocType;

  const handleBack = () => actorRef.send({ type: 'ON_PREVIOUS_PAGE' });
  const handleNext = () => {
    if (documentReference) {
      actorRef.send({ type: 'ON_COMPLETE_REGISTRATION' });
    } else {
      actorRef.send({
        type: 'ON_NEXT_PAGE',
        goTo: PAGES.REGISTRATION_COMPLETE,
        payload: undefined,
      });
    }
  };

  // Label shifts with upload state so the primary action always matches intent:
  // "Skip" (nothing uploaded), "Next" (uploaded but not finalized),
  // "Complete Registration" (uploaded and ready to finish).
  const nextLabel = documentReference ? 'Complete Registration' : uploaded ? 'Next' : 'Skip';

  // Neutral: the document type is unknown until the user answers the classify
  // question, which only appears once a file has been selected. [MCV-1593]
  const title = 'Upload your document';
  const subtitle =
    'Upload your CV or credentialing packet to complete your registration. We will ask which one it is.';

  return (
    <Container>
      <Header cyTag={cvUploadTags.title} title={title} subtitle={subtitle} />

      <FormContent scrollOnPhones={false}>
        <CVUploadForm actorRef={actorRef} docType={docType} />
      </FormContent>

      <Footer
        cyTags={footerTags}
        handleBack={handleBack}
        loadingPercent={75}
        next={{
          onClick: handleNext,
          label: nextLabel,
          cyTag: cvUploadTags.nextButton,
        }}
      />
    </Container>
  );
}
